import { Box, Button, Stack, Typography } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { Link } from "react-router-dom"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import { RefreshTableButton } from "../common/refreshTableButton"
import { usePostBotListState } from "../../hooks/usePostBotListState"
import type { Occurrence } from "../../interface/occurrences"
import { useGetPlacementHistoryQuery } from "../../services/publications/placementsApi"
import { occurrencePath, occurrenceTime } from "../../utils/occurrences"
import {
  TemplateQueryFeedback,
  useTemplateRowCount,
} from "../templates/templateQueryFeedback"
import { missingRecordedValue } from "./recordedValue"

const columns: GridColDef<Occurrence>[] = (
  [
    {
      field: "id",
      headerName: "Отправка",
      width: 200,
      renderCell: ({ row }) => (
        <Button
          component={Link}
          to={occurrencePath(row.id)}
          aria-label={`Открыть отправку ${row.id}`}
        >
          Открыть отправку
        </Button>
      ),
    },
    { field: "purpose", headerName: "Назначение", width: 120 },
    { field: "status", headerName: "Доставка", width: 150 },
    {
      field: "channelId",
      headerName: "Записанный канал",
      width: 220,
      valueFormatter: ({ value }) => value ?? missingRecordedValue,
    },
    {
      field: "createdAt",
      headerName: "Создано (МСК)",
      width: 210,
      valueFormatter: ({ value }) =>
        occurrenceTime(value) ?? missingRecordedValue,
    },
    {
      field: "messageIds",
      headerName: "Известные messageIds",
      minWidth: 200,
      flex: 1,
      valueFormatter: ({ value }) =>
        value === null ? missingRecordedValue : JSON.stringify(value),
    },
    {
      field: "pin",
      headerName: "Закрепление",
      width: 160,
      valueGetter: ({ row }) => row.pin?.status ?? missingRecordedValue,
    },
    {
      field: "deletePrevious",
      headerName: "Удаление предыдущих",
      width: 190,
      valueGetter: ({ row }) =>
        row.deletePrevious?.status ?? missingRecordedValue,
    },
  ] satisfies GridColDef<Occurrence>[]
).map((column) => ({ ...column, sortable: false, filterable: false }))

export function OccurrenceHistoryTable({
  placementId,
}: {
  placementId: string
}) {
  const list = usePostBotListState({
    resource: "history",
    key: `history-${placementId}`,
  })
  const query = useGetPlacementHistoryQuery({
    id: placementId,
    ...list.listArgs,
  })
  const page = !query.error ? query.currentData : undefined
  const rowCount = useTemplateRowCount(placementId, page?.total)
  return (
    <Stack spacing={2}>
      <Typography>
        История запланированных отправок размещения, включая legacy-записи.
        Preview в эту историю не входит.
      </Typography>
      <TemplateQueryFeedback
        query={query}
        empty={page?.data.length === 0 && "Запланированных отправок пока нет"}
      />
      <RefreshTableButton
        label="Обновить историю"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      />
      <Box sx={{ width: "100%", minHeight: 200 }}>
        <DataGrid
          {...list.gridProps}
          rows={page?.data ?? []}
          columns={columns}
          rowCount={rowCount}
          loading={query.isFetching}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
    </Stack>
  )
}
